import { Input as AntInput } from "antd"
import type { InputRef } from "antd"
import React, { type ComponentProps, useRef } from "react"

import { useCapitalizingValue } from "./use-capitalizing-value.ts"

interface TextInputProps extends ComponentProps<typeof AntInput> {
  /** Opts a field (email, password, URL, code/ID…) out of auto-capitalization. */
  disableAutoCapitalize?: boolean
}

/**
 * Drop-in replacement for antd's `Input` that keeps the field's first letter
 * capital as the user types. See `useCapitalizingValue` for the mechanics.
 */
export const TextInput = React.forwardRef<InputRef, TextInputProps>(
  ({ disableAutoCapitalize, value, defaultValue, onChange, ...props }, ref) => {
    const localRef = useRef<InputRef>(null)

    const setRefs = (node: InputRef | null) => {
      localRef.current = node
      if (typeof ref === "function") ref(node)
      else if (ref)
        (ref as React.MutableRefObject<InputRef | null>).current = node
    }

    const { currentValue, handleChange } = useCapitalizingValue({
      // Text-only wrapper — antd's broader Input value type (numbers,
      // string arrays) doesn't apply to how this component is ever used.
      value: value as string | undefined,
      defaultValue: defaultValue as string | undefined,
      disableAutoCapitalize,
      maxLength: props.maxLength,
      onChange,
      getElement: () => localRef.current?.input
    })

    return (
      <AntInput
        ref={setRefs}
        value={currentValue}
        onChange={handleChange}
        {...props}
      />
    )
  }
)

TextInput.displayName = "TextInput"

export default TextInput
